import React, { createContext, useContext, useCallback, useMemo } from "react";
import { db } from "../db/state";
import { useValue } from "../lib/db/react";
import { put } from "../lib/db/db";
import {
  SubscriptionState,
  SubscriptionTier,
  PremiumFeature,
} from "./types";

const defaultSubscription: SubscriptionState = {
  tier: "free",
  expiresAt: null,
  stripeCustomerId: null,
};

interface SubscriptionContextValue {
  state: SubscriptionState;
  isPremium: boolean;
  canAccess: (feature: PremiumFeature) => boolean;
  setSubscription: (state: SubscriptionState) => void;
}

const SubscriptionContext = createContext<SubscriptionContextValue>({
  state: defaultSubscription,
  isPremium: false,
  canAccess: () => false,
  setSubscription: () => {},
});

export const useSubscriptionContext = () => useContext(SubscriptionContext);

export const SubscriptionProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const raw = useValue(db, "subscription");
  const state: SubscriptionState = (raw as SubscriptionState) || defaultSubscription;

  const isPremium = state.tier === "premium" && (
    state.expiresAt === null || state.expiresAt * 1000 > Date.now()
  );

  const setSubscription = useCallback((next: SubscriptionState) => {
    put(db, "subscription", next);
  }, []);

  const canAccess = useCallback(
    (feature: PremiumFeature): boolean => {
      // All features available in premium; free tier has limited access
      if (isPremium) return true;
      // Free tier features
      const freeFeatures: PremiumFeature[] = [];
      return freeFeatures.includes(feature);
    },
    [isPremium]
  );

  const value = useMemo(
    () => ({ state, isPremium, canAccess, setSubscription }),
    [state, isPremium, canAccess, setSubscription]
  );

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};
